import type { AdvancedSearchFilters, BasicSearchFilters } from "@/types/search";

export interface SearchableProperty {
  id: string;
  title: string;
  slug?: string | null;
  project_name?: string | null;
  locality?: string | null;
  city?: string | null;
  address?: string | null;
  price?: number | null;
  price_min?: number | null;
  price_max?: number | null;
  price_on_request?: boolean | null;
  property_type?: string | null;
  listing_type?: string | null;
  area_sqft?: number | null;
  area_min?: number | null;
  area_max?: number | null;
  bedrooms?: number | null;
  bedrooms_options?: number[] | null;
  bathrooms?: number | null;
  images?: string[] | null;
  description?: string | null;
  is_featured?: boolean | null;
  latitude?: number | null;
  longitude?: number | null;
  developer_name?: string | null;
  possession_date?: string | null;
  possession_stage?: string | null;
  status?: string | null;
  amenities?: string[] | null;
}

export interface SearchSuggestion {
  id: string;
  type: "project" | "locality" | "configuration";
  label: string;
  sublabel?: string;
  count: number;
  filters: Partial<AdvancedSearchFilters>;
}

const PROPERTY_TYPE_ALIASES: Record<string, string[]> = {
  commercial: ["commercial", "office", "shop", "warehouse", "retail", "industrial"],
  plot: ["plot", "land"],
  land: ["land", "plot"],
  house: ["house", "villa", "bungalow"],
  villa: ["villa", "house", "bungalow"],
  apartment: ["apartment", "flat", "residence", "penthouse"]
};

export const resolvePropertyTypeFilters = (propertyType?: string): string[] => {
  const normalized = propertyType?.trim().toLowerCase();
  if (!normalized || normalized === "all") return [];
  return PROPERTY_TYPE_ALIASES[normalized] ?? [normalized];
};

/**
 * Extracts numeric BHK count from free text (e.g. "3 BHK", "2bhk", "4 bedroom", "1 bed", "3rk")
 */
export function extractBhkFromText(text?: string | null): number | null {
  if (!text) return null;
  const match = text.match(/\b([1-9])\s*(?:bhk|bed|bedroom|rk|b)\b/i) || text.match(/\b([1-9])bhk\b/i);
  if (match) {
    const parsed = parseInt(match[1], 10);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
}

/**
 * Matches a property against a configuration requirement (e.g. 1, 2, 3, 4, 5, "2 BHK", "3 BHK", "5+ BHK")
 */
export function matchesConfiguration(
  property: SearchableProperty,
  config?: number | string | null
): boolean {
  if (config === undefined || config === null || config === "" || config === "all") {
    return true;
  }

  const configStr = config.toString().trim();
  if (!configStr || configStr.toLowerCase() === "all" || configStr.toLowerCase() === "all bhk") {
    return true;
  }

  const is5Plus =
    configStr.includes("5+") ||
    configStr.toLowerCase().includes("5+ bhk") ||
    configStr.toLowerCase().includes("5 plus");

  if (is5Plus) {
    if ((property.bedrooms ?? 0) >= 5) return true;
    if (Array.isArray(property.bedrooms_options) && property.bedrooms_options.some((b) => b >= 5)) {
      return true;
    }
    return false;
  }

  const numeric =
    typeof config === "number"
      ? config
      : extractBhkFromText(configStr) ?? (parseInt(configStr, 10) || null);

  if (numeric !== null && !isNaN(numeric)) {
    if (property.bedrooms === numeric) return true;
    if (Array.isArray(property.bedrooms_options) && property.bedrooms_options.includes(numeric)) {
      return true;
    }
    const bhkPattern = new RegExp(`\\b${numeric}\\s*bhk\\b`, "i");
    if (property.title && bhkPattern.test(property.title)) return true;
    return false;
  }

  // Handle penthouse or studio
  const lower = configStr.toLowerCase();
  if (lower.includes("penthouse")) {
    return (
      (property.property_type?.toLowerCase().includes("penthouse") ||
        property.title?.toLowerCase().includes("penthouse")) ??
      false
    );
  }
  if (lower.includes("studio")) {
    return property.bedrooms === 0 || (property.title?.toLowerCase().includes("studio") ?? false);
  }

  return false;
}

/**
 * Matches a property against locality, city, or address
 */
export function matchesLocality(
  property: SearchableProperty,
  targetLocality?: string | null
): boolean {
  if (!targetLocality || !targetLocality.trim()) return true;
  const target = targetLocality.trim().toLowerCase();
  if (target === "all" || target === "all localities" || target === "any") return true;

  const loc = (property.locality || "").trim().toLowerCase();
  const city = (property.city || "").trim().toLowerCase();
  const address = (property.address || "").trim().toLowerCase();

  if (loc.includes(target) || target.includes(loc)) return true;
  if (city.includes(target) || target.includes(city)) return true;
  if (address.includes(target)) return true;

  // Compare key locality words (ignoring common noise like 'west', 'east', 'road', 'mumbai')
  const keyTokens = target
    .split(/[\s,]+/)
    .map((w) => w.trim())
    .filter((w) => w.length > 2 && !["west", "east", "road", "mumbai"].includes(w));

  if (keyTokens.length > 0) {
    return keyTokens.some((w) => loc.includes(w) || address.includes(w) || city.includes(w));
  }

  return false;
}

/**
 * Matches a property against project name, title, or developer name
 */
export function matchesProjectName(
  property: SearchableProperty,
  targetProject?: string | null
): boolean {
  if (!targetProject || !targetProject.trim()) return true;
  const target = targetProject.trim().toLowerCase();
  if (target === "all" || target === "any") return true;

  const projectName = (property.project_name || "").trim().toLowerCase();
  const title = (property.title || "").trim().toLowerCase();
  const devName = (property.developer_name || "").trim().toLowerCase();

  if (projectName && (projectName.includes(target) || target.includes(projectName))) return true;
  if (title.includes(target) || target.includes(title)) return true;
  if (devName && devName.includes(target)) return true;

  const tokens = target
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 2 && !["project", "tower", "heights", "residency"].includes(t));

  if (tokens.length > 0) {
    return tokens.every((t) => projectName.includes(t) || title.includes(t) || devName.includes(t));
  }

  return false;
}

/**
 * Multi-token smart query matcher. Handles compound queries like:
 * - "Prestige Daffodils" (Project)
 * - "3 BHK Bandra" (Config + Locality)
 * - "2 BHK Khar" (Config + Locality)
 * - "Le Repos 2 BHK" (Project + Config)
 * - "Lodha Worli" (Project + Locality)
 */
export function matchesUnifiedQuery(
  property: SearchableProperty,
  query?: string | null
): boolean {
  if (!query || !query.trim()) return true;
  const raw = query.trim().toLowerCase();

  // 1. If BHK pattern is present, ensure configuration matches
  const targetBhk = extractBhkFromText(raw);
  if (targetBhk !== null) {
    if (!matchesConfiguration(property, targetBhk)) {
      return false;
    }
  }

  // 2. Strip BHK from text to match remaining tokens
  const textWithoutBhk = raw.replace(/\b[1-9]\s*(?:bhk|bed|bedroom|rk|b)\b/gi, "").trim();
  if (!textWithoutBhk) {
    // Query was strictly BHK (e.g. "3 BHK"), which already passed!
    return true;
  }

  // 3. Remove common stop words
  const STOP_WORDS = new Set([
    "in",
    "at",
    "near",
    "for",
    "the",
    "and",
    "or",
    "of",
    "to",
    "with",
    "flat",
    "flats",
    "apartment",
    "apartments",
    "property",
    "properties",
    "residence",
    "luxury",
    "home",
    "homes",
    "mumbai",
    "project"
  ]);

  const tokens = textWithoutBhk
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !STOP_WORDS.has(t));

  if (tokens.length === 0) return true;

  const pName = (property.project_name || "").toLowerCase();
  const pTitle = (property.title || "").toLowerCase();
  const pLoc = (property.locality || "").toLowerCase();
  const pCity = (property.city || "").toLowerCase();
  const pDev = (property.developer_name || "").toLowerCase();
  const pAddr = (property.address || "").toLowerCase();
  const pType = (property.property_type || "").toLowerCase();

  // Every significant token in the query must match at least one relevant field
  return tokens.every((token) => {
    return (
      pName.includes(token) ||
      pTitle.includes(token) ||
      pLoc.includes(token) ||
      pCity.includes(token) ||
      pDev.includes(token) ||
      pAddr.includes(token) ||
      pType.includes(token)
    );
  });
}

/**
 * Applies all filters to a list of properties
 */
export function filterProperties<T extends SearchableProperty>(
  items: T[],
  filters?: AdvancedSearchFilters | BasicSearchFilters | null
): T[] {
  if (!filters) return items;
  let filtered = [...items];

  // 1. Keyword / Freeform search (searches across project name, configuration, locality)
  const keyword = (filters as any).keyword?.toString().trim();
  if (keyword) {
    filtered = filtered.filter((p) => matchesUnifiedQuery(p, keyword));
  }

  // 2. Explicit Project Name filter
  const projectName =
    (filters as any).projectName?.toString().trim() ||
    (filters as any).project_name?.toString().trim();
  if (projectName) {
    filtered = filtered.filter((p) => matchesProjectName(p, projectName));
  }

  // 3. Explicit Locality / Location / City filter
  const locality =
    (filters as any).locality?.toString().trim() ||
    (filters as any).location?.toString().trim();
  if (locality) {
    filtered = filtered.filter((p) => matchesLocality(p, locality));
  }

  const city = (filters as any).city?.toString().trim().toLowerCase();
  if (city && city !== "all" && city !== "mumbai") {
    filtered = filtered.filter((p) => p.city && p.city.toLowerCase().includes(city));
  }

  // 4. Explicit Configuration / Bedrooms filter
  const configuration = (filters as any).configuration?.toString().trim();
  if (configuration) {
    filtered = filtered.filter((p) => matchesConfiguration(p, configuration));
  } else if ((filters as any).bedrooms !== undefined && (filters as any).bedrooms !== null) {
    const bhk = Number((filters as any).bedrooms);
    if (!isNaN(bhk) && bhk > 0) {
      filtered = filtered.filter((p) => matchesConfiguration(p, bhk));
    }
  }

  // 5. Property Type filter
  const propType = (filters as any).propertyType || (filters as any).property_type;
  if (propType && propType !== "all") {
    const types = resolvePropertyTypeFilters(propType);
    if (types.length > 0) {
      filtered = filtered.filter((p) =>
        p.property_type ? types.includes(p.property_type.toLowerCase()) : false
      );
    }
  }

  // 6. Price Range filter
  const priceRange = (filters as any).priceRange as [number, number] | undefined;
  if (Array.isArray(priceRange)) {
    const [lo, hi] = priceRange;
    if (lo > 0) {
      filtered = filtered.filter((p) => {
        const val = p.price ?? p.price_min ?? 0;
        return val >= lo;
      });
    }
    if (hi > 0 && hi < 500000000) {
      filtered = filtered.filter((p) => {
        const val = p.price ?? p.price_max ?? Infinity;
        return val <= hi;
      });
    }
  }

  const minPrice = (filters as any).minPrice || (filters as any).priceMin;
  const maxPrice = (filters as any).maxPrice || (filters as any).priceMax;
  if (minPrice && Number(minPrice) > 0) {
    filtered = filtered.filter((p) => {
      const val = p.price ?? p.price_min ?? 0;
      return val >= Number(minPrice);
    });
  }
  if (maxPrice && Number(maxPrice) > 0 && Number(maxPrice) < 500000000) {
    filtered = filtered.filter((p) => {
      const val = p.price ?? p.price_max ?? Infinity;
      return val <= Number(maxPrice);
    });
  }

  // 7. Area Range filter
  const areaRange = (filters as any).areaRange as [number, number] | undefined;
  if (Array.isArray(areaRange)) {
    const [lo, hi] = areaRange;
    if (lo > 0) {
      filtered = filtered.filter((p) => {
        const val = p.area_sqft ?? p.area_min ?? 0;
        return val >= lo;
      });
    }
    if (hi > 0 && hi < 50000) {
      filtered = filtered.filter((p) => {
        const val = p.area_sqft ?? p.area_max ?? Infinity;
        return val <= hi;
      });
    }
  }

  // 8. Bathrooms filter
  const bathrooms = (filters as any).bathrooms;
  if (bathrooms && Number(bathrooms) > 0) {
    filtered = filtered.filter((p) => (p.bathrooms ?? 0) >= Number(bathrooms));
  }

  // 9. Amenities filter
  const amenities = (filters as any).amenities;
  if (Array.isArray(amenities) && amenities.length > 0) {
    filtered = filtered.filter((p) => {
      if (!p.amenities || !Array.isArray(p.amenities)) return false;
      return amenities.every((req) =>
        p.amenities!.some((a) => a.toLowerCase().includes(req.toLowerCase()))
      );
    });
  }

  return filtered;
}

/**
 * Extracts list of unique, cleaned localities with listing counts
 */
export function extractUniqueLocalities(properties: SearchableProperty[]): { locality: string; count: number }[] {
  const counts: Record<string, number> = {};
  properties.forEach((p) => {
    const raw = p.locality?.trim();
    if (!raw) return;
    counts[raw] = (counts[raw] || 0) + 1;
  });

  return Object.entries(counts)
    .map(([locality, count]) => ({ locality, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Extracts list of unique projects with counts and localities
 */
export function extractUniqueProjects(properties: SearchableProperty[]): { project: string; locality: string | null; count: number }[] {
  const projectsMap: Map<string, { locality: string | null; count: number }> = new Map();

  properties.forEach((p) => {
    const name = (p.project_name || p.title)?.trim();
    if (!name) return;

    const existing = projectsMap.get(name);
    if (existing) {
      existing.count += 1;
    } else {
      projectsMap.set(name, {
        locality: p.locality ?? null,
        count: 1
      });
    }
  });

  return Array.from(projectsMap.entries())
    .map(([project, meta]) => ({
      project,
      locality: meta.locality,
      count: meta.count
    }))
    .sort((a, b) => a.project.localeCompare(b.project));
}

/**
 * Extracts available BHK configurations with property counts
 */
export function extractUniqueConfigurations(properties: SearchableProperty[]): { bhk: string; bedrooms: number; count: number }[] {
  const counts: Record<number, number> = {};
  properties.forEach((p) => {
    const set = new Set<number>();
    if (typeof p.bedrooms === "number" && p.bedrooms > 0) set.add(p.bedrooms);
    if (Array.isArray(p.bedrooms_options)) {
      p.bedrooms_options.forEach((b) => {
        if (typeof b === "number" && b > 0) set.add(b);
      });
    }
    set.forEach((b) => {
      counts[b] = (counts[b] || 0) + 1;
    });
  });

  return Object.entries(counts)
    .map(([bedStr, count]) => {
      const bedrooms = parseInt(bedStr, 10);
      const bhk = bedrooms >= 5 ? `${bedrooms}+ BHK` : `${bedrooms} BHK`;
      return { bhk, bedrooms, count };
    })
    .sort((a, b) => a.bedrooms - b.bedrooms);
}

/**
 * Generates instant suggestions for search dropdown
 */
export function generateSearchSuggestions(
  properties: SearchableProperty[],
  query: string,
  limit: number = 8
): SearchSuggestion[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const suggestions: SearchSuggestion[] = [];

  // 1. Check Configuration matches (e.g. user typed "3" or "3 bhk")
  const bhkNum = extractBhkFromText(q) ?? (parseInt(q, 10) || null);
  if (bhkNum !== null && bhkNum >= 1 && bhkNum <= 8) {
    const matchingProps = properties.filter((p) => matchesConfiguration(p, bhkNum));
    if (matchingProps.length > 0) {
      const label = `${bhkNum} BHK Properties`;
      suggestions.push({
        id: `config-${bhkNum}`,
        type: "configuration",
        label,
        sublabel: `${matchingProps.length} verified listings in Mumbai`,
        count: matchingProps.length,
        filters: { configuration: `${bhkNum} BHK`, bedrooms: bhkNum }
      });
    }
  }

  // 2. Check Locality matches
  const localities = extractUniqueLocalities(properties);
  const matchingLocalities = localities.filter(
    (l) => l.locality.toLowerCase().includes(q) || q.includes(l.locality.toLowerCase())
  );
  matchingLocalities.slice(0, 3).forEach((loc) => {
    suggestions.push({
      id: `loc-${loc.locality}`,
      type: "locality",
      label: loc.locality,
      sublabel: `Locality • ${loc.count} available properties`,
      count: loc.count,
      filters: { locality: loc.locality, location: loc.locality }
    });
  });

  // 3. Check Project Name matches
  const projects = extractUniqueProjects(properties);
  const matchingProjects = projects.filter((p) =>
    p.project.toLowerCase().includes(q)
  );
  matchingProjects.slice(0, 4).forEach((proj) => {
    suggestions.push({
      id: `proj-${proj.project}`,
      type: "project",
      label: proj.project,
      sublabel: proj.locality ? `Project in ${proj.locality}` : "Residential Project",
      count: proj.count,
      filters: { projectName: proj.project, keyword: proj.project }
    });
  });

  return suggestions.slice(0, limit);
}
